import { useCallback, useEffect, useState } from 'react';
import type { MailboxConnectionResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

// Every mounted instance (the app-wide banner, the page's own) registers its loader here, so a `reload`
// after an in-place IMAP reconnect refreshes all of them, not just the caller's.
const readers = new Set<() => Promise<void>>();

// Loads the caller's own mailbox connection state (none / active / expired) for
// the settings surface (IMM-86.3 / IMM-86.4). Mirrors `useMyMemberProfile`: each
// route can stand alone, and `reload` lets a mutation refresh the state in place.
export function useMailboxConnection(workspaceId: string | undefined) {
  const [connection, setConnection] = useState<MailboxConnectionResponse | null>(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (workspaceId === undefined) return;

    setLoadError(false);

    try {
      const result = await apiClient.mailbox.getConnection({ pathParams: { workspaceId } });

      if (result.ok) setConnection(result.body);
      else setLoadError(true);
    } catch {
      setLoadError(true);
    }
  }, [workspaceId]);

  useEffect(() => {
    readers.add(load);
    void load();

    return () => {
      readers.delete(load);
    };
  }, [load]);

  const reload = useCallback(() => Promise.all([...readers].map((read) => read())).then(() => undefined), []);

  return { connection, loadError, reload };
}
